import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import { Switch } from 'src/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';

const defaultFormData = {
  name: '',
  description: '',
  is_active: true,
};

const FieldLabel = ({ htmlFor, children, required }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[12.5px] font-[560] text-foreground mb-1.5 tracking-[.005em]"
  >
    {children}
    {required && <span className="text-destructive ml-0.5">*</span>}
  </label>
);

const fieldBase =
  'w-full h-9 px-3 py-2 rounded-lg border border-input bg-card text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:opacity-50 disabled:cursor-not-allowed';

export const VendorModal = ({ onSubmit, onCancel, initialData, loading }) => {
  const [formData, setFormData] = useState({
    ...defaultFormData,
    ...(initialData && {
      name: initialData.name ?? '',
      description: initialData.description ?? '',
      is_active: initialData.is_active ?? true,
    }),
  });

  const isEditMode = Boolean(initialData?.id);
  const buttonText = isEditMode ? 'Save Changes' : 'Create Vendor';
  const loadingText = isEditMode ? 'Saving...' : 'Creating...';

  const set = (key) => (e) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const isFormValid =
    formData.name.trim() !== '' && formData.description.trim() !== '';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    onSubmit({
      name: formData.name.trim(),
      description: formData.description.trim(),
      is_active: formData.is_active,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-[22px] py-[18px] space-y-4">
        <div>
          <FieldLabel htmlFor="vendor-name" required>
            Vendor Name
          </FieldLabel>
          <input
            id="vendor-name"
            className={fieldBase}
            value={formData.name}
            onChange={set('name')}
            placeholder="e.g. Acme Staffing Pvt. Ltd."
            disabled={loading}
          />
        </div>

        <div>
          <FieldLabel htmlFor="vendor-description" required>
            Description
          </FieldLabel>
          <textarea
            id="vendor-description"
            className={cn(fieldBase, 'resize-y min-h-[80px] leading-[1.65]')}
            value={formData.description}
            onChange={set('description')}
            placeholder="Brief description of the vendor…"
            disabled={loading}
          />
        </div>

        <div className="border-t border-border pt-4 flex items-center gap-3">
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) =>
              setFormData((p) => ({ ...p, is_active: checked }))
            }
            disabled={loading}
          />
          <span className="text-[13px] font-medium text-foreground">
            {formData.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Pinned footer */}
      <div className="flex justify-end gap-2 px-[22px] py-[14px] border-t border-border bg-brand-bg-subtle shrink-0">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={loading || !isFormValid}>
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {loadingText}
            </>
          ) : (
            buttonText
          )}
        </Button>
      </div>
    </form>
  );
};

export default VendorModal;
